import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { supportedLanguages } from '@i18n/index';
const languageNames = {
    es: 'Español',
    en: 'English',
    it: 'Italiano',
    fr: 'Français'
};
export const LanguageSelector = () => {
    const { i18n, t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const selectorRef = useRef(null);
    const triggerRef = useRef(null);
    const activeLanguage = supportedLanguages.includes(i18n.language)
        ? i18n.language
        : 'es';
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!selectorRef.current?.contains(event.target))
                setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    return (_jsxs(Selector, { ref: selectorRef, onBlur: (event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
                setIsOpen(false);
        }, onKeyDown: (event) => {
            if (event.key === 'Escape' && isOpen) {
                event.stopPropagation();
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        }, children: [_jsxs(Trigger, { ref: triggerRef, type: "button", "aria-label": `${t('navigation.language')}: ${languageNames[activeLanguage]}`, "aria-expanded": isOpen, "aria-controls": "language-options", onClick: () => setIsOpen((current) => !current), children: [_jsx("span", { children: activeLanguage.toUpperCase() }), _jsx(ChevronDown, { size: 14, "aria-hidden": "true" })] }), isOpen && (_jsx(Menu, { id: "language-options", "aria-label": t('navigation.language'), children: supportedLanguages.map((language) => (_jsxs(Option, { type: "button", lang: language, "aria-pressed": activeLanguage === language, onClick: () => {
                        void i18n.changeLanguage(language);
                        setIsOpen(false);
                        triggerRef.current?.focus();
                    }, children: [languageNames[language], _jsx("span", { "aria-hidden": "true", children: language.toUpperCase() })] }, language))) }))] }));
};
const Selector = styled.div `
  position: relative;
`;
const Trigger = styled.button `
  display: inline-flex;
  min-height: 44px;
  min-width: 56px;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 0 0.3rem;
  color: ${({ theme }) => theme.colors.navy900};
  background: transparent;
  font-size: 0.8125rem;
  font-weight: 700;
  &:hover {
    background: ${({ theme }) => theme.colors.mint50};
  }
`;
const Menu = styled.div `
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 50;
  display: grid;
  min-width: 180px;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 6px;
  padding: 0.35rem;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.soft};
`;
const Option = styled.button `
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 0;
  border-radius: 3px;
  padding: 0.55rem 0.65rem;
  color: ${({ theme }) => theme.colors.navy900};
  background: transparent;
  text-align: left;
  font-size: 0.9375rem;
  > span {
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.75rem;
  }
  &:hover,
  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.colors.surface};
  }
`;
